/**
 * @file src/utils/debugLog.js
 * @description Console lines that only a developer chasing a specific subsystem wants to see.
 *
 * The rule this exists to serve: a line either earns a place in EVERY session's console, or it goes
 * behind a flag. `console.warn` should mean something is wrong — when routine progress reports share
 * the stream with real warnings, the real ones stop being read.
 *
 * Several subsystems already had this gate hand-rolled (`window.__DEBUG_COSMIC_LFO`,
 * `window.__DEBUG_MIDI`, `window.__DEBUG_ENGINE_STACKS`, …). This is the one implementation of it,
 * so adding a gate is a line rather than another copy of the same six.
 *
 * @example
 * const debug = createDebugLog('__DEBUG_PARAMS', '[ParameterManager]');
 * debug('dimension switched', { paramName, dimensionId }); // silent unless the flag is set
 *
 * Turn one on from the console: `window.__DEBUG_PARAMS = true` (no reload needed — the flag is read
 * per call).
 */

/**
 * Build a gated logger.
 * @param {string} flag the `window` property that turns these lines on, e.g. `'__DEBUG_PARAMS'`.
 * @param {string} prefix the bracketed subsystem tag every line carries, e.g. `'[ParameterManager]'`.
 * @returns {(message: string, ...detail: unknown[]) => void}
 */
export function createDebugLog(flag, prefix) {
  return function debug(message, ...detail) {
    try {
      if (typeof window === 'undefined' || !window[flag]) return;
      console.debug(`${prefix} ${message}`, ...detail);
    } catch (_) {
      // Logging must never be the thing that breaks a caller.
    }
  };
}
